import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { logError } from '../utils/errorHandler';
import { useToastContext } from '../context/ToastContext';

const SessionManager: React.FC = () => {
  const { logout, isAuthenticated } = useAuth();
  const { showWarning } = useToastContext();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const SESSION_TIMEOUT = 300 * 60 * 1000; // 30分
  const WARNING_TIME = 5 * 60 * 1000; // 5分前に警告

  // セッションタイムアウト処理
  const handleSessionTimeout = useCallback(() => {
    try {
      showWarning('セッションがタイムアウトしました。再度ログインしてください。');
      logout();
    } catch (error) {
      logError(error as Error, 'SessionManager.handleSessionTimeout');
      // フォールバック: alertを使用
      alert('セッションがタイムアウトしました。再度ログインしてください。');
      logout();
    }
  }, [logout, showWarning]);

  // セッション警告表示
  const showSessionWarning = useCallback(() => {
    try {
      const confirmed = window.confirm('あと5分でセッションがタイムアウトします。\n継続しますか？');

      if (confirmed) {
        resetActivityTimer();
      }
    } catch (error) {
      logError(error as Error, 'SessionManager.showSessionWarning');
    }
  }, []);

  // アクティビティタイマーのリセット
  const resetActivityTimer = useCallback(() => {
    try {
      // 既存のタイマーをクリア
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }

      // 認証されている場合のみタイマーを設定
      if (isAuthenticated()) {
        // 警告タイマー設定
        warningTimeoutRef.current = setTimeout(() => {
          showSessionWarning();
        }, SESSION_TIMEOUT - WARNING_TIME);

        // タイムアウトタイマー設定
        timeoutRef.current = setTimeout(() => {
          handleSessionTimeout();
        }, SESSION_TIMEOUT);
      }
    } catch (error) {
      logError(error as Error, 'SessionManager.resetActivityTimer');
    }
  }, [isAuthenticated, handleSessionTimeout, showSessionWarning, SESSION_TIMEOUT, WARNING_TIME]);

  // ユーザーアクティビティの監視
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      try {
        if (isAuthenticated()) {
          resetActivityTimer();
        }
      } catch (error) {
        logError(error as Error, 'SessionManager.handleActivity');
      }
    };

    // イベントリスナーを追加
    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // 初期タイマー設定
    try {
      if (isAuthenticated()) {
        resetActivityTimer();
      }
    } catch (error) {
      logError(error as Error, 'SessionManager.initialTimer');
    }

    // クリーンアップ
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [isAuthenticated, resetActivityTimer]);

  // コンポーネントは何もレンダリングしない
  return null;
};

export default SessionManager;
